import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table, Tag } from 'antd';
import style from './prevention.scss';
import ThresholdValueCard from '../components/thresholdValueCard/thresholdValueCard';
import { InfoCircleOutlined } from '@ant-design/icons';
import ConfigureDrawer from '../../trm/configureInstanceDashboard/ConfigureDrawer';
import * as api from '../../../services/trm/trmServices';
import TablePagination from '../../cm/common/TablePagination';
const networkLayerObject = new api.NetworkLayer();

function PreventionTable({
  scoreExplainabilityData,
  setRolesState,
  alertInfo,
  trmActions,
  isLoading
}) {
  const drawerRef = useRef(null);

  const [renderData, setRenderData] = useState([]);
  const [dropdownRoleValue, setDropdownRoleValue] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 10
  });

  useEffect(() => {
    let initialRoles = [];
    if (Array.isArray(renderData)) {
      renderData.map((data) => {
        initialRoles = [...initialRoles, ...data.entityRole];
      });
      setDropdownRoleValue(initialRoles);
    }
  }, [renderData]);

  useEffect(() => {
    setRolesState(dropdownRoleValue);
  }, [dropdownRoleValue]);

  const onOpenDrawer = useCallback((triggerName) => {
    if (triggerName) {
      networkLayerObject
        .trmScenarioTypologiesListAPI({
          scenarioID: alertInfo.scenarioId
        })
        .then((res) => {
          const variantInfo = JSON.parse(alertInfo.triggeredRules)?.find(
            (f) => f.ruleName === triggerName
          ) || { rule: {} };
          const typologyInfo = res.typologies.find(
            (typology) => typology.id === variantInfo.typologyId
          );
          const typologyInfoRules = JSON.parse(typologyInfo.rules)?.find(
            (f) => f.ruleName === triggerName
          );
          drawerRef.current?.showDrawer(0, {
            ...typologyInfo,
            ...typologyInfoRules,
            variantName: triggerName,
            typologyId: variantInfo.typologyId,
            typologiesReadOnly: res.typologies
          });
        });
    }
  }, []);

  const preventionAlertColors = (key) => {
    let scoreKey = key.toLowerCase();
    let colorCodeLevels = Object.values(trmActions?.data || {});
    for (let level of colorCodeLevels) {
      if (
        level &&
        level.displayName &&
        level.displayName.toLowerCase() === scoreKey
      ) {
        if (level.alertLevel === 'High') return '#E94E43';
        if (level.alertLevel === 'Good') return '#EB922A';
        if (level.alertLevel === 'Low') return '#49C93D';
      }
    }
    return '#00000';
  };

  const columns = [
    {
      title: 'Typology Name',
      dataIndex: 'typologyName',
      key: 'typologyName',
      width: '20%',
      render: (typologyName, record, index) => {
        const obj = {
          children: (
            <span
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
              {typologyName}
            </span>
          ),
          props: {}
        };
        const trueIndex =
          index + paginationInfo.pageSize * (paginationInfo.current - 1);
        if (
          index >= 1 &&
          typologyName === renderData[trueIndex - 1].typologyName
        ) {
          obj.props.rowSpan = 0;
        } else {
          for (
            let i = 0;
            trueIndex + i !== renderData.length &&
            typologyName === renderData[trueIndex + i].typologyName;
            i += 1
          ) {
            obj.props.rowSpan = i + 1;
          }
        }
        return obj;
      }
    },
    {
      title: 'Variant Name',
      dataIndex: 'triggerName',
      key: 'triggerName',
      width: '19%',
      render: (triggerName, record, index) => {
        const trueIndex =
          index + paginationInfo.pageSize * (paginationInfo.current - 1);
        const obj = {
          children: (
            <span
              style={{
                display: 'flex',
                gap: '10px'
              }}>
              {triggerName}
              <InfoCircleOutlined
                onClick={() => onOpenDrawer(triggerName)}
                style={{ fontSize: '16px', color: '#08c', cursor: 'pointer' }}
              />
            </span>
          ),
          props: {}
        };
        if (
          index >= 1 &&
          triggerName === renderData[trueIndex - 1].triggerName
        ) {
          obj.props.rowSpan = 0;
        } else {
          for (
            let i = 0;
            trueIndex + i !== renderData.length &&
            triggerName === renderData[trueIndex + i].triggerName;
            i += 1
          ) {
            obj.props.rowSpan = i + 1;
          }
        }
        return obj;
      }
    },
    {
      title: 'Group By',
      dataIndex: 'entityRole',
      key: 'entityRole',
      width: '10%',
      render: (entityRole) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {entityRole
            ? entityRole.map((ent) => (
                <Tag
                  style={{
                    borderRadius: '25px',
                    width: 'fit-content',
                    alignSelf: 'end'
                  }}
                  color="red">
                  {ent}
                </Tag>
              ))
            : ''}
        </div>
      )
    },
    {
      title: (
        <div className={style.combinedColumns}>
          <p>Function Name</p> <p>Value</p>
        </div>
      ),
      dataIndex: 'functionValue', // Update the dataIndex to 'functionValue'
      key: 'functionValue',
      width: '25%',
      render: (functionValueArray, record, index) => {
        const obj = {
          children: <ThresholdValueCard value={functionValueArray} />,
          props: {}
        };
        return obj;
      }
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: '10%',
      render: (text) => (
        <div className={style.actionCard}>
          <p
            style={{
              textTransform: 'capitalize',
              color: preventionAlertColors(text)
            }}>
            {text}
          </p>
        </div>
      )
    },
    {
      title: 'Additional Info',
      dataIndex: 'additionalInfo',
      key: 'additionalInfo',
      render: (text) => (
        <div>
          <p>{text}</p>
        </div>
      )
    }
  ];

  useEffect(() => {
    if (scoreExplainabilityData) {
      onRenderData(scoreExplainabilityData);
    }
  }, [scoreExplainabilityData]);

  const onRenderData = (dataSource) => {
    const flattenedData = Object.keys(dataSource)
      .reduce((acc, typologyKey) => {
        const typologyName = typologyKey;
        const triggers = dataSource[typologyKey];

        const infoDetails = triggers?.map((trigger, index) => {
          const functionValueArray = Object.entries(trigger.functionValue).map(
            ([key, value]) => ({
              key,
              value: value.value
            })
          );

          return {
            key: `${typologyName}-${index}`,
            typologyName: typologyName,
            triggerName: trigger.triggerName,
            functionValue: functionValueArray,
            action: trigger.action,
            additionalInfo: trigger.additionalInfo,
            rowSpan: index === 0 ? triggers.length : 0,
            entityRole: trigger.entityRole
          };
        });

        return [...acc, ...infoDetails];
      }, [])
      .filter(
        (v, i, a) =>
          a.findIndex((v2) =>
            ['triggerName', 'functionValue'].every(
              (k) => v2[k]?.toString() === v[k]?.toString()
            )
          ) === i
      );

    setRenderData(flattenedData);
  };

  const handleChange = (pagination, pageSize) => {
    setPaginationInfo({
      current: pagination,
      pageSize: pageSize
    });
  };

  return (
    <>
      <Table
        // onChange={handleChange}
        className={style.ScoreTable}
        columns={columns}
        dataSource={renderData}
        bordered
        loading={isLoading}
        pagination={{
          total: renderData?.length || 0,
          ...paginationInfo,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: handleChange,
          showTotal: (total) => (
            <TablePagination
              selectedItemsCount={null}
              total={total}
              currentPage={paginationInfo.current}
              pageSize={paginationInfo.pageSize}
            />
          )
        }}
      />
      <ConfigureDrawer typologies={[]} ref={drawerRef} isReadOnly={true} />
    </>
  );
}

export default PreventionTable;
